/** Display helpers shared across console pages. */

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '—'
  if (bytes === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / 1024 ** exponent
  return `${value.toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`
}

export function formatNumber(value: number | null | undefined): string {
  return value == null ? '—' : value.toLocaleString()
}

/** Renders an ISO timestamp in the browser's local zone. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString()
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString()
}

/**
 * DICOM PersonName uses ^ to separate the five name components. Show it in a
 * readable order without discarding information.
 */
export function formatPersonName(pn: string | null | undefined): string {
  if (!pn) return '—'
  const [family = '', given = '', middle = '', prefix = '', suffix = ''] = pn.split('^')
  const parts = [prefix, given, middle, family, suffix].filter(Boolean)
  return parts.length ? parts.join(' ') : pn
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return '—'
  if (seconds < 60) return `${Math.round(seconds)}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  if (hours < 24) return `${hours}h ${minutes}m`
  return `${Math.floor(hours / 24)}d ${hours % 24}h`
}

/** Maps a backend state string onto the console's status colour tokens. */
export function stateTone(state: string | null | undefined): 'ok' | 'warn' | 'error' | 'idle' {
  switch (state) {
    case 'OK':
    case 'COMPLETED':
    case 'SUCCESS':
    case 'PRESENT':
    // A listener that is bound and an AE row that is on are both "working".
    case 'BOUND':
    case 'ENABLED':
      return 'ok'
    case 'WARNING':
    case 'DEGRADED':
    case 'UNAVAILABLE':
    case 'RETRY_WAIT':
    case 'PARTIAL':
    // Transient by design: the supervisor is mid-rebind, or has not got to it
    // yet. Neither is a fault, but neither is finished either.
    case 'REBINDING':
    case 'STARTING':
      return 'warn'
    case 'ERROR':
    case 'CRITICAL':
    case 'OFFLINE':
    case 'FAILED':
    case 'DEAD_LETTER':
    case 'CORRUPT':
      return 'error'
    default:
      return 'idle'
  }
}
